import { useMemo } from "react";
import { Table } from "@heroui/react";
import type { PeriodFormat, PeriodStatPoint } from "../model/types";

export interface StatisticsTableProps {
  data: PeriodStatPoint[];
  format: PeriodFormat;
}

export const StatisticsTable = ({ data, format }: StatisticsTableProps) => {
  const totals = useMemo(() => {
    const totalImpressions = data.reduce(
      (sum, r) => sum + (r.impressions || 0),
      0,
    );
    const totalClicks = data.reduce((sum, r) => sum + (r.clicks || 0), 0);
    const totalSpent = data.reduce((sum, r) => sum + (r.spent || 0), 0);
    const totalConversions = data.reduce(
      (sum, r) => sum + (r.conversions || 0),
      0,
    );
    const avgCtr =
      totalImpressions > 0
        ? Number(((totalClicks / totalImpressions) * 100).toFixed(2))
        : 0;
    const avgCpa =
      totalConversions > 0 ? Math.round(totalSpent / totalConversions) : 0;

    return {
      impressions: totalImpressions,
      clicks: totalClicks,
      spent: totalSpent,
      conversions: totalConversions,
      ctr: avgCtr,
      cpa: avgCpa,
    };
  }, [data]);

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-neutral-400">
        {format === "year" ? "Статистика по месяцам" : "Статистика по периоду"}
      </div>
      <Table>
        <Table.ScrollContainer>
          <Table.Content aria-label="Статистика за период">
            <Table.Header>
              <Table.Column isRowHeader>
                {format === "year" ? "Месяц" : "Дата"}
              </Table.Column>
              <Table.Column>Показы</Table.Column>
              <Table.Column>Клики</Table.Column>
              <Table.Column>CTR</Table.Column>
              <Table.Column>Расход</Table.Column>
              <Table.Column>Конверсии</Table.Column>
              <Table.Column>CPA</Table.Column>
            </Table.Header>
            <Table.Body>
              {data.map((row) => (
                <Table.Row key={row.date}>
                  <Table.Cell>{row.date}</Table.Cell>
                  <Table.Cell>
                    {row.impressions.toLocaleString("ru-RU")}
                  </Table.Cell>
                  <Table.Cell>{row.clicks.toLocaleString("ru-RU")}</Table.Cell>
                  <Table.Cell>{row.ctr}%</Table.Cell>
                  <Table.Cell>{row.spent.toLocaleString("ru-RU")} ₽</Table.Cell>
                  <Table.Cell>{row.conversions}</Table.Cell>
                  <Table.Cell>{row.cpa.toLocaleString("ru-RU")} ₽</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>

        {data.length > 0 && (
          <Table.Footer>
            <div className="flex flex-wrap items-center justify-between w-full text-xs ">
              <span>Итого:</span>
              <span>{totals.impressions.toLocaleString("ru-RU")}</span>
              <span>{totals.clicks.toLocaleString("ru-RU")}</span>
              <span>{totals.ctr}%</span>
              <span>{Math.round(totals.spent).toLocaleString("ru-RU")} ₽</span>
              <span>{totals.conversions.toLocaleString("ru-RU")}</span>
              <span>
                {totals.cpa > 0
                  ? `${totals.cpa.toLocaleString("ru-RU")} ₽`
                  : "—"}
              </span>
            </div>
          </Table.Footer>
        )}
      </Table>
    </div>
  );
};
